import { includesHeniSearch } from '../isHeniSearch';
import { HENI_TAG_LABEL } from '#lib/websites/news/constants/heni-tag.js';
import { NewsRoutes } from '#lib/websites/news/constants/routes.js';
import type { SearchNewsQuery } from '#lib/websites/news/mutations/__generated__/searchNews.generated.js';
import type { Section } from '$global/features/search/components/QuerySearch/Search';
import type { SearchResultProps } from '$global/features/search/components/QuerySearch/SearchResult';
import { getImageUrl } from '$global/utils/getImageUrl';

// Section titles are matched on by NewsSearchSuggestions' renderTags snippet to
// pick the right tag component, so they live here as the single source of truth.
export const NewsSearchSectionTitles = {
	Heni: 'HENI',
	Artists: 'ARTISTS',
	Geotags: 'GEOTAGS',
	Articles: 'ARTICLES',
} as const;

// Article results carry the data the renderTags snippet needs to render a
// NewNewsCard-style row (image + title + category/geotag/artist pills). They are
// stored in `Section.results` (typed SearchResultProps[]) and read back via this
// type in the snippet — `image`/`href`/`line1` map onto SearchResultProps, the
// rest are extra fields the snippet consumes.
export interface NewsArticleResult extends SearchResultProps {
	geotagName: string | null;
	artistNames: string[];
	categories: string[];
}

// Artists and geotags render as NewNewsCard-style pills (ArtistCardTag /
// GeoCardTag) that link to their filtered news feed; articles render as a
// NewNewsCard-style row (image + title + pills) linking to the article's own
// URL — swapped to the mobile-article route when the global nav is hidden,
// exactly as NewNewsCard does in the feed.
export const getSections = (
	data: SearchNewsQuery | undefined,
	showGlobalNav: boolean,
	searchValue?: string
): Section[] => {
	const hits = data?.searchNews ?? [];

	// A search containing "heni" surfaces the HENI feed itself as the first
	// suggestion. Substring rather than exact (unlike the Enter key), since the
	// suggestion is opt-in — "heni talks" should still offer it.
	// Like the artist/geotag pills it carries only `line1`; HeniCardTag builds its
	// own href via getHeniTagLink, so no href is needed here.
	const heniResults: SearchResultProps[] = includesHeniSearch(searchValue)
		? [{ line1: HENI_TAG_LABEL } as SearchResultProps]
		: [];

	// Artist/geotag results only carry `line1` (the tag name) — the renderTags
	// snippet renders ArtistCardTag/GeoCardTag, which build their own href via
	// getTagLink, so no href is needed here.
	const artists = hits
		.map((hit) => hit.artist)
		.filter((artist): artist is NonNullable<typeof artist> => Boolean(artist))
		.map((artist) => ({ line1: artist.name }) as SearchResultProps);

	const geotags = hits
		.map((hit) => hit.geotag)
		.filter((geotag): geotag is NonNullable<typeof geotag> => Boolean(geotag))
		.map((geotag) => ({ line1: geotag.name }) as SearchResultProps);

	const articles = hits
		.map((hit) => hit.article)
		.filter((article): article is NonNullable<typeof article> =>
			Boolean(article)
		)
		.map(
			(article) =>
				({
					image: {
						url: getImageUrl(article.image?.filename_disk, '512px'),
						width: article.image?.width,
						height: article.image?.height,
					},
					href: showGlobalNav
						? article.url
						: article.url?.replace(
								NewsRoutes.Article,
								NewsRoutes.MobileArticle
							),
					imageRounded: false,
					line1: article.title ?? '',
					geotagName: article.geotag?.name ?? null,
					artistNames: (article.artists ?? [])
						.map((entry) => entry?.HENI_News_Artists_id?.name)
						.filter((name): name is string => Boolean(name)),
					categories: (article.categories ?? [])
						.map((entry) => entry?.HENI_News_Categories_id?.name)
						.filter((name): name is string => Boolean(name)),
				}) as NewsArticleResult
		);

	return [
		...(heniResults.length > 0
			? [
					{
						title: NewsSearchSectionTitles.Heni,
						// The row's only result is the HENI pill, which spells out
						// "HENI" itself — a "HENI" label beside it just says it twice.
						displayTitle: '',
						variant: 'tags',
						results: heniResults,
					},
				]
			: []),
		...(artists.length > 0
			? [
					{
						title: NewsSearchSectionTitles.Artists,
						variant: 'tags',
						results: artists,
					},
				]
			: []),
		...(geotags.length > 0
			? [
					{
						title: NewsSearchSectionTitles.Geotags,
						variant: 'tags',
						results: geotags,
					},
				]
			: []),
		...(articles.length > 0
			? [
					{
						title: NewsSearchSectionTitles.Articles,
						variant: 'tags',
						results: articles,
					},
				]
			: []),
	] as Section[];
};
